<template>
  <div class="sm:c-col-4 md:c-col-8 lg:c-col-6 p-login-container">
    <div>
      <p-form
        @submit="onSubmit"
        v-slot="{ invalid }"
      >
        <h3 class="c-text-h3 ui-mb-6">
          Faça login com sua conta Estratégia
        </h3>

        <!-- Email -->
        <div class="c-row ui-mb-4">
          <validation-provider
            class="c-col"
            rules="required|email"
            v-slot="{ errors }"
            name="Email"
          >
            <p-input
              v-model="user.email"
              data-cy="loginField"
              label="Email*"
              name="loginField"
              :error-message="errors[0]"
              type="email"
            />
          </validation-provider>
        </div>

        <!-- Senha / Esqueci minha senha -->
        <div class="c-row ui-mb-4">
          <div class="c-col">
            <div class="ui-flex ui-flex-col sm:ui-flex-row ui-w-full">
              <!-- Senha -->
              <validation-provider
                class="ui-flex-auto sm:ui-flex-1"
                rules="required|min:5"
                v-slot="{ errors }"
                :custom-messages="{
                  required: 'Senha é obrigatória',
                  min: 'A senha é muito curta'
                }"
              >
                <input-password
                  v-model="user.password"
                  data-cy="passwordField"
                  label="Senha*"
                  name="passwordField"
                  :error-message="errors[0]"
                />
              </validation-provider>

              <!-- Esqueci minha senha -->
              <div class="ui-ml-0 sm:ui-ml-4">
                <div class="ui-h-input ui-flex ui-items-center">
                  <template v-if="$slots.forgotPassword">
                    <slot name="forgotPassword" />
                  </template>
                  <router-link
                    v-else
                    :to="{ name: 'esqueci-senha', query: { ...$route.query, email: user.email } }"
                    data-cy="forgotPasswordLink"
                    class="c-text-hyperlink c-text-gray"
                  >
                    <c-link class="padding-remover">
                      Esqueci minha senha
                    </c-link>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Codigo de verificação -->
        <div
          class="ui-mb-10"
          v-if="isMFARequired"
        >
          <p-input
            v-model="user.mfaCode"
            mask="000000"
            :maxlength="6"
            type="text"
            label="Código de verificação"
            input-class="mfa-code-input"
            class="ui-mb-2"
          />
          <div class="q-pt-xs">
            <c-button
              data-cy="registerButton"
              size="sm"
              type="submit"
              color="primary"
              :disabled="mfaRetryCounter > 0"
              @click="() => requestAnotherMFA()"
            >
              {{ MFAButtonLabel }}
              <icon
                name="next"
                class="c-icon--right"
              />
            </c-button>
          </div>
        </div>

        <!-- Botão Continuar -->
        <div class="c-row">
          <div class="c-col">
            <vue-hcaptcha
              v-if="showCaptcha"
              ref="captcha"
              :sitekey="$env.VUE_APP_HCAPTCHA_SITE_KEY"
              @verify="response => setCaptchaToken(response)"
              @expired="() => clearCaptchaToken()"
              @error="() => clearCaptchaToken()"
            />
            <c-button
              v-else
              :disabled="invalid"
              data-cy="registerButton"
              size="lg"
              type="submit"
              class="ui-w-full"
              :loading="isFormSubmitting"
            >
              Continuar
            </c-button>
          </div>
        </div>

        <div class="c-row ui-my-4">
          <div class="c-col">
            <separator />
          </div>
          <div class="c-d-flex">
            <p class="c-text-b2 c-text-gray-300 ui-pt-1">
              Ou
            </p>
          </div>
          <div class="c-col">
            <separator />
          </div>
        </div>

        <div class="c-row ui-mb-4">
          <div class="c-col">
            <template v-if="$slots.accessKey">
              <slot name="accessKey" />
            </template>
            <router-link
              v-else
              :to="{ name: 'enviar-chave-de-acesso', query: { ...$route.query, email: user.email } }"
              data-cy="accessKeyLoginLink"
            >
              <c-button
                :disabled="isFormSubmitting"
                data-cy="accessKeyLoginButton"
                size="lg"
                class="ui-w-full"
                outline
              >
                Login via chave de acesso
              </c-button>
            </router-link>
          </div>
        </div>

        <!-- Social Login Botões -->
        <div
          v-if="showSocialLogin"
          class="c-row ui-mt-8"
        >
          <div class="c-col">
            <separator />
          </div>

          <div class="c-d-flex">
            <social-login-button
              provider="facebook"
              @logged-in="handleLoggedIn"
              :prevent-redirection="preventRedirection"
              :goal-id="goalId"
            />
            <social-login-button
              provider="google"
              @logged-in="handleLoggedIn"
              :prevent-redirection="preventRedirection"
              :goal-id="goalId"
            />
            <social-login-button
              provider="apple"
              @logged-in="handleLoggedIn"
              :prevent-redirection="preventRedirection"
              :goal-id="goalId"
            />
          </div>

          <div class="c-col">
            <separator />
          </div>
        </div>
      </p-form>

      <template v-if="showSignup">
        <div class="c-row ui-mt-8">
          <div class="c-col">
            <div class="ui-flex ui-flex-col c-text-gray">
              <span class="c-text-b1 c-text-gray-500 ui-mb-2"><strong>Ainda não é aluno?</strong></span>
              <template v-if="$slots.cadastro">
                <slot name="cadastro" />
              </template>
              <router-link
                v-else
                :to="{ name: 'cadastro-rapido', query: { ...$route.query } }"
                class="width-fit"
                data-cy="cadastroLink"
              >
                <c-link class="padding-remover">
                  Cadastre-se
                </c-link>
              </router-link>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { CButton, Icon, CLink } from '@estrategiahq/coruja-web-ui';
import InputPassword from '@/components/input-password';
import PInput from '@/components/p-input';
import PForm from '@/components/p-form';
import Separator from '@/components/separator';
import SocialLoginButton from '@/components/p-social-login-button';
import GenericModal from '@/modals/generic';
import { requestCanceledError } from '@/utils/errors';
import { ValidationProvider } from 'vee-validate';
import { LOGOUT_REASON_KEY, LOGOUT_REASON_MESSAGES, ACCOUNTS_OAUTH_CALLBACK_URL } from '@/consts';
import VueHcaptcha from '@hcaptcha/vue-hcaptcha';
import { extractUtmFromCookies } from '@/utils/utm';
import { getQueryParam, redirectToTarget } from '@/utils/url';
import { getToken } from '@/services/token';

export default {
  name: 'PLogin',
  components: {
    SocialLoginButton,
    PInput,
    CButton,
    Icon,
    CLink,
    InputPassword,
    Separator,
    ValidationProvider,
    PForm,
    VueHcaptcha,
  },
  props: {
    preventRedirection: {
      type: Boolean,
      default: false,
    },
    goalId: {
      type: String,
      default: null,
    },
  },
  data: () => {
    return {
      user: {
        email: '',
        password: '',
        mfaCode: '',
      },
      oauth_state: '',
      isFormSubmitting: false,
      isCaptchaRequired: false,
      captchaToken: '',
      showSocialLogin: false,
      showSignup: false,
      isMFARequired: false,
      MFAButtonLabel: 'Enviar outro código',
      mfaRetryCounter: 10,

    };
  },
  watch: {
    mfaRetryCounter: {
      handler (value) {
        if (value > 0) {
          setTimeout(() => {
            this.mfaRetryCounter--;
            this.updateMFAButtonLabel();
          }, 1000);
        }
      },
      immediate: true,
    },
  },
  mounted () {
    const email = this.$route.query.email;
    if (email) {
      this.user.email = email.trim();
    }

    this.oauth_state = getQueryParam('state');
    const hideSocial = this.getBoolParam('hide-social');
    if (!hideSocial) {
      this.$socialLogin.someActive().then((someActive) => {
        this.showSocialLogin = someActive;
      });
    }

    const hideSignup = this.getBoolParam('hide-signup');
    if (!hideSignup) {
      this.showSignup = true;
    }

    const logoutReason = sessionStorage.getItem(LOGOUT_REASON_KEY);
    if (logoutReason) {
      this.showModalLogoutReason(logoutReason);
    }
    sessionStorage.removeItem(LOGOUT_REASON_KEY);
  },
  computed: {
    showCaptcha() {
      return this.isCaptchaRequired && (!this.captchaToken || this.captchaToken === '');
    },
  },
  methods: {
    updateMFAButtonLabel () {
      if (this.mfaRetryCounter > 0) {
        this.MFAButtonLabel = `Enviar outro código em ${this.mfaRetryCounter} segundos`;
      } else {
        this.MFAButtonLabel = 'Enviar outro código';
      }
    },
    getBoolParam(name) {
      try {
        const val = JSON.parse(this.$route.query[name]);
        return !!val;
      } catch (_) {
        return false;
      }
    },
    async persistUtmIfExists() {
      try {
        const utm = extractUtmFromCookies();
        if (utm) {
          this.$services.bff.setUtm(utm);
        }
      } catch (_) {
        // possível erro não deve ser mostrado ao usuário
      }
    },
    async onSubmit() {
      this.isFormSubmitting = true;
      try {
        const logindata = await this.$services.accounts.login(this.user.email, this.user.password, this.captchaToken, this.user.mfaCode, this.oauth_state, this.goalId);
        this.$emit('logged-in', logindata);
        this.loginSuccessHandler(logindata);
      } catch (e) {
        if (e === requestCanceledError) {
          return;
        }
        await this.handleLoginErrors(e?.response?.data?.error?.tag);
      } finally {
        this.isFormSubmitting = false;
      }
    },
    showModalLogoutReason(logoutReason) {
      const message = LOGOUT_REASON_MESSAGES[logoutReason];
      if (!message) return;

      this.$modal.show({
        component: GenericModal,
        overlayOpacity: 0.5,
        props: {
          title: '',
          subTitle: message,
          type: 'error',
          confirmButton: {
            label: 'Fechar',
            onClick: () => this.$modal.hide(),
          },
        },
      });
    },
    loginSuccessHandler(logindata) {
      if (this.$route.query.parceiro) {
        localStorage.setItem('partner@estrategia', this.$route.query.parceiro);
      }
      // Limpa Estado
      this.isMFARequired = false;

      if (this.preventRedirection) {
        return;
      }
      if (logindata.oauth_callback_url) {
        localStorage.setItem(ACCOUNTS_OAUTH_CALLBACK_URL, logindata.oauth_callback_url);
        if (localStorage.getItem(ACCOUNTS_OAUTH_CALLBACK_URL)) {
          this.$router.push({ name: 'oauth-callback-redirect' });
        }
        localStorage.removeItem('last_login_failed');
        return;
      }

      this.$services.events.notifyManualLogin();
      this.persistUtmIfExists();
      localStorage.removeItem('last_login_failed');

      if (this.$route.query.returnTo) {
        try {
          const returnToQuery = this.$route.query.returnToQuery ?
            JSON.parse(this.$route.query.returnToQuery) : {};

          this.$router.push({
            name: this.$route.query.returnTo,
            query: returnToQuery,
          });
          return;
        } catch (e) {
          console.error('Erro ao fazer redirecionamento de volta:', e);
        }
      }

      redirectToTarget({
        target: this.$route.query.target,
        token: getToken(),
        allowedHosts: this.$env.VUE_APP_ALLOWED_TARGET_HOSTS.split(','),
        fallback: () => this.$router.push('/'),
      });
    },
    async handleLoginErrors (accountsError) {
      this.isCaptchaRequired = false;
      localStorage.setItem('last_login_failed', true);
      switch (accountsError) {
      case 'LEAKED_PASSWORD':
        this.$router.push({ name: 'esqueci-senha', query: { ...this.$route.query, email: this.user.email } });
        this.$toast.show({
          type: 'error',
          text: 'Sua senha foi exposta em um vazamento de dados e sua conta foi bloqueada. Para desbloquear, redefina sua senha.',
          timeout: 10000,
        });
        break;
      case 'REQUIRE_PASSWORD_CHANGE':
        this.$router.push({ name: 'esqueci-senha', query: { ...this.$route.query, email: this.user.email } });
        this.$toast.show({
          type: 'error',
          text: 'Sua senha expirou. Redefina uma nova senha.',
          timeout: 5000,
        });
        break;
      case 'REQUIRE_MFA_CODE':
        this.isMFARequired = true;
        this.mfaRetryCounter = 30;
        this.$toast.show({
          type: 'success',
          text: 'Código de verificação enviado para o seu email.',
          timeout: 5000,
        });
        break;
      case 'EMAIL_NOT_CONFIRMED':
        this.$router.push({
          name: 'cadastro-confirme-seu-email',
          query: {
            ...this.$route.query,
            email: this.user.email,
          },
        });
        break;
      case 'INVALID_CREDENTIALS':
      case 'AUTH.NOT_AUTHENTICATED':
        this.$toast.show({
          type: 'error',
          text: 'Usuário ou senha inválidos.',
          timeout: 3000,
        });
        break;
      case 'ERROR_CONFIRMING_CAPTCHA':
      case 'INVALID_CAPTCHA':
        this.isCaptchaRequired = true;
        this.captchaToken = '';
        this.$toast.show({
          type: 'error',
          text: 'Captcha inválido ou expirado',
          timeout: 1000,
        });
        break;
      case 'INVALID_MFA_CODE':
        this.$toast.show({
          type: 'error',
          text: 'Código de verificação inválido.',
          timeout: 3000,
        });
        break;
      default:
        this.$toast.show({
          type: 'error',
          text: accountsError || 'undefined',
          timeout: 3000,
        });
        break;
      }
    },
    async requestAnotherMFA () {
      this.user.mfaCode = '';
      try {
        await this.$services.accounts.login(this.user.email, this.user.password, this.captchaToken, this.user.mfaCode, this.oauth_state);
      } catch (e) {
        await this.handleLoginErrors(e?.response?.data?.error?.tag);
      }
    },
    setCaptchaToken (token) {
      this.captchaToken = token;
      localStorage.setItem('captcha_token', token);
    },
    clearCaptchaToken () {
      this.captchaToken = '';
      localStorage.removeItem('captcha_token');
    },
    handleLoggedIn(val) {
      this.$emit('logged-in', val);
    },
  },
};
</script>

<style scoped>
.p-login-container {
  padding: 2rem !important;
}

.mfa-code-input {
  font-family: ui-monospace, monospace;
  font-size: 20px;
  text-align: center;
  letter-spacing: 5px;
}

.padding-remover {
  padding: 0 !important;
  border: none !important;
}

.width-fit {
  width: fit-content !important;
}
</style>
